import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Lightbulb, 
  Plus,
  RefreshCw,
  TrendingUp,
  ExternalLink,
  Lock,
  Check,
  Info,
  Star,
  Target,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { toast } from 'sonner';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../components/ui/hover-card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Opportunities() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [opportunities, setOpportunities] = useState([]);
  const [providerLabel, setProviderLabel] = useState('');
  const [diagnostic, setDiagnostic] = useState(null);
  const [loading, setLoading] = useState(false);
  const [intentFilter, setIntentFilter] = useState('all');
  const [geoFilter, setGeoFilter] = useState('all');
  const [competitionFilter, setCompetitionFilter] = useState('all');
  const [quickWins, setQuickWins] = useState(false);
  const [targetZones, setTargetZones] = useState([]);
  const [addingKeyword, setAddingKeyword] = useState(null);
  const [addedKeywords, setAddedKeywords] = useState(new Set());
  // Sort state — null = no sort = backend order (default by priority_score desc)
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('desc');

  // Toggle sort: 1st click → desc, 2nd → asc, 3rd → reset
  const handleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('desc');
    } else if (sortDir === 'desc') {
      setSortDir('asc');
    } else {
      setSortKey(null);
      setSortDir('desc');
    }
  };

  // Map a sort key to a comparable scalar. Return null for "missing" so the
  // comparator can always push them to the bottom (regardless of asc/desc).
  const sortValue = (opp, key) => {
    switch (key) {
      case 'keyword': return (opp.keyword || '').toLowerCase();
      case 'zone': return opp.geo_multiplier ?? 0;
      case 'intent': {
        // Order intents by economic value: transactional > commercial > local > navigational > info
        const order = { transactional: 5, commercial: 4, local: 3, navigational: 2, informational: 1 };
        return order[opp.intent] ?? 0;
      }
      case 'score': return opp.priority_score ?? 0;
      case 'position': {
        // Unranked (null/0) always last
        const p = opp.position;
        return (p == null || p === 0) ? null : Number(p);
      }
      case 'volume': return opp.search_volume ?? 0;
      case 'cpc': {
        // Coerce defensively (some providers return strings like "1.50").
        // Null / 0 always sink to the bottom in both directions.
        if (opp.cpc == null || opp.cpc === '') return null;
        const n = Number(opp.cpc);
        return Number.isFinite(n) && n > 0 ? n : null;
      }
      case 'competition': {
        if (opp.competition == null || opp.competition === '') return null;
        const n = Number(opp.competition);
        return Number.isFinite(n) ? n : null;
      }
      case 'potential': {
        const order = { high: 3, medium: 2, low: 1 };
        return order[opp.potential] ?? 0;
      }
      default: return 0;
    }
  };

  const SortableTh = ({ k, label, align = 'left', tooltip, className = '' }) => {
    const active = sortKey === k;
    const Icon = !active ? ArrowUpDown : sortDir === 'desc' ? ArrowDown : ArrowUp;
    return (
      <th className={className}>
        <button
          type="button"
          onClick={() => handleSort(k)}
          className={`inline-flex items-center gap-1 hover:text-[#002FA7] transition-colors ${align === 'right' ? 'flex-row-reverse w-full justify-end' : ''} ${active ? 'text-[#002FA7] font-semibold' : ''}`}
          title={tooltip || `Trier par ${label}`}
          data-testid={`sort-${k}`}
        >
          <span>{label}</span>
          <Icon className={`w-3 h-3 ${active ? 'opacity-100' : 'opacity-40'}`} />
        </button>
      </th>
    );
  };

  useEffect(() => {
    fetchClients();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchClients = async () => {
    try {
      const { data } = await axios.get(`${API}/clients`, { withCredentials: true });
      setClients(data);
      if (data.length > 0) {
        setSelectedClient(data[0].id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    }
  };

  useEffect(() => {
    if (selectedClient) {
      fetchOpportunities();
    }
  }, [selectedClient]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOpportunities = async () => {
    if (!selectedClient) return;
    
    setLoading(true);
    setOpportunities([]);
    setAddedKeywords(new Set());
    setDiagnostic(null);
    
    try {
      const { data } = await axios.get(`${API}/opportunities/${selectedClient}?limit=50`, { withCredentials: true });
      setOpportunities(data.opportunities || []);
      setProviderLabel(data.provider_label || '');
      setTargetZones(data.target_zones || []);
      setDiagnostic({
        error: data.error || null,
        provider_configured: data.provider_configured !== false,
        raw_count: data.raw_count || 0,
        filtered_out: data.filtered_out_as_tracked || 0,
        response_keys: data.response_keys || [],
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  const addAsKeyword = async (opportunity, isPriority = false) => {
    setAddingKeyword(opportunity.keyword);
    try {
      await axios.post(`${API}/opportunities/${selectedClient}/add`, {
        keyword: opportunity.keyword,
        url: opportunity.url,
        position: opportunity.position,
        search_volume: opportunity.search_volume,
        priority: opportunity.potential === 'high' ? 'high' : 'medium',
        is_priority: isPriority
      }, { withCredentials: true });
      
      setAddedKeywords(prev => new Set([...prev, opportunity.keyword]));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setAddingKeyword(null);
    }
  };

  const getPotentialBadge = (potential) => {
    switch (potential) {
      case 'high':
        return <span className="text-xs font-mono px-2 py-1 rounded bg-green-50 text-green-700">Fort potentiel</span>;
      case 'medium':
        return <span className="text-xs font-mono px-2 py-1 rounded bg-yellow-50 text-yellow-700">Potentiel moyen</span>;
      default:
        return <span className="text-xs font-mono px-2 py-1 rounded bg-neutral-100 text-neutral-600">Potentiel faible</span>;
    }
  };

  const INTENT_META = {
    transactional: { label: 'Transactionnel', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', priority: 1 },
    commercial:    { label: 'Commercial',     cls: 'bg-blue-50 text-blue-700 border-blue-200',          priority: 2 },
    local:         { label: 'Local',          cls: 'bg-purple-50 text-purple-700 border-purple-200',    priority: 3 },
    navigational:  { label: 'Navigation',     cls: 'bg-cyan-50 text-cyan-700 border-cyan-200',          priority: 4 },
    brand:         { label: 'Marque',         cls: 'bg-pink-50 text-pink-700 border-pink-200',          priority: 5 },
    informational: { label: 'Information',    cls: 'bg-neutral-100 text-neutral-600 border-neutral-200', priority: 6 },
  };

  const getIntentBadge = (intent) => {
    if (!intent || !INTENT_META[intent]) return <span className="text-xs text-neutral-400">—</span>;
    const m = INTENT_META[intent];
    return (
      <span className={`text-xs px-2 py-0.5 rounded border ${m.cls}`} data-testid={`intent-${intent}`}>
        {m.label}
      </span>
    );
  };

  const GEO_BADGE = {
    own_zone:      { label: '🎯 Ma zone',       cls: 'bg-emerald-100 text-emerald-800 border-emerald-300 font-semibold' },
    own_region:    { label: '🟢 Ma région',     cls: 'bg-green-50 text-green-700 border-green-200' },
    local_generic: { label: '📍 Local',          cls: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
    national:      { label: '🌐 National',       cls: 'bg-neutral-100 text-neutral-600 border-neutral-200' },
    local_other:   { label: '⚠️ Autre zone',    cls: 'bg-rose-50 text-rose-700 border-rose-200' },
    no_zones:      { label: '—',                 cls: 'bg-neutral-50 text-neutral-400 border-neutral-200' },
  };

  const getGeoBadge = (opp) => {
    const meta = GEO_BADGE[opp.geo_match] || GEO_BADGE.no_zones;
    const zoneText = opp.geo_label && opp.geo_label !== 'National' ? ` · ${opp.geo_label}` : '';
    return (
      <span
        className={`text-xs px-2 py-0.5 rounded border whitespace-nowrap ${meta.cls}`}
        data-testid={`geo-${opp.geo_match}`}
        title={`${meta.label} (×${opp.geo_multiplier})${zoneText}`}
      >
        {meta.label}
      </span>
    );
  };

  // Maximum priority score across the current displayed opportunities — used
  // to scale the score bar relatively (a "Score 5000" feels different on a
  // domain whose max is 50000 vs a domain whose max is 8000).
  const maxScore = useMemo(
    () => opportunities.reduce((m, o) => Math.max(m, o.priority_score || 0), 0),
    [opportunities]
  );

  const getScoreBadge = (score) => {
    const s = score || 0;
    if (s === 0) return <span className="text-xs text-neutral-300">—</span>;
    const ratio = maxScore > 0 ? s / maxScore : 0;
    let cls = 'bg-neutral-100 text-neutral-600';
    if (ratio >= 0.66) cls = 'bg-emerald-50 text-emerald-700 border border-emerald-200';
    else if (ratio >= 0.33) cls = 'bg-amber-50 text-amber-700 border border-amber-200';
    return (
      <span
        className={`inline-block min-w-[60px] text-xs font-mono font-bold px-2 py-1 rounded ${cls}`}
        data-testid="priority-score"
        title={`Score basé sur volume × CPC × intention ÷ √position`}
      >
        {s.toLocaleString()}
      </span>
    );
  };

  const selectedClientData = clients.find(c => c.id === selectedClient);

  /**
   * Normalize a competition value to a percentage 0-100.
   * - Haloscan returns 0-100 (integer)
   * - DataForSEO returns 0-1 (float)
   * - Some providers return strings like "low" / "medium" / "high"
   */
  const normalizeCompetition = (raw) => {
    if (raw === null || raw === undefined || raw === '') return null;
    if (typeof raw === 'string') {
      const s = raw.toLowerCase().trim();
      if (s === 'low') return 20;
      if (s === 'medium' || s === 'mid') return 55;
      if (s === 'high') return 85;
      const parsed = parseFloat(s);
      if (Number.isNaN(parsed)) return null;
      return parsed <= 1 ? Math.round(parsed * 100) : Math.round(parsed);
    }
    if (typeof raw !== 'number' || Number.isNaN(raw)) return null;
    return raw <= 1 ? Math.round(raw * 100) : Math.round(raw);
  };

  const getCompetitionBadge = (raw) => {
    const pct = normalizeCompetition(raw);
    if (pct === null) return <span className="text-xs text-neutral-300">—</span>;
    let cls = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    let label = 'Faible';
    if (pct >= 67) {
      cls = 'bg-rose-50 text-rose-700 border-rose-200';
      label = 'Forte';
    } else if (pct >= 34) {
      cls = 'bg-amber-50 text-amber-700 border-amber-200';
      label = 'Moyenne';
    }
    return (
      <span
        className={`inline-flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded border ${cls}`}
        data-testid="competition-badge"
        title={`Concurrence ${label.toLowerCase()} (${pct}/100). Plus c'est bas, plus le mot-clé est facile à conquérir.`}
      >
        {pct}
        <span className="text-[10px] opacity-70">/ 100</span>
      </span>
    );
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="opportunities-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">
                Opportunités
              </h1>
              <span className="flex items-center gap-1 text-xs font-mono px-2 py-1 bg-yellow-50 text-yellow-700 rounded">
                <Lock className="w-3 h-3" />
                TECHNIQUE
              </span>
            </div>
            <p className="text-neutral-500" data-testid="opportunities-subtitle">
              Mots-clés non suivis sur lesquels le site est positionné{providerLabel ? ` (via ${providerLabel})` : ''}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => setQuickWins(v => !v)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                quickWins
                  ? 'bg-emerald-600 border-emerald-600 text-white shadow-sm'
                  : 'bg-white border-emerald-200 text-emerald-700 hover:bg-emerald-50'
              }`}
              title="Position 4–20 + Concurrence ≤ moyenne + CPC > 1 € — les mots-clés où peu d'effort = gros ROI"
              data-testid="quick-wins-toggle"
            >
              <Target className="w-3.5 h-3.5" />
              {quickWins ? 'Top Quick Wins ✓' : 'Top Quick Wins'}
            </button>
            <Select value={geoFilter} onValueChange={setGeoFilter}>
              <SelectTrigger className="w-[200px]" data-testid="geo-filter">
                <SelectValue placeholder="Zone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes zones</SelectItem>
                <SelectItem value="my_zones">🎯 Mes zones uniquement</SelectItem>
                <SelectItem value="local_relevant">📍 Local pertinent (mes zones + générique)</SelectItem>
                <SelectItem value="national">🌐 National uniquement</SelectItem>
                <SelectItem value="exclude_others">Cacher les autres zones</SelectItem>
              </SelectContent>
            </Select>
            <Select value={competitionFilter} onValueChange={setCompetitionFilter}>
              <SelectTrigger className="w-[180px]" data-testid="competition-filter">
                <SelectValue placeholder="Concurrence" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toute concurrence</SelectItem>
                <SelectItem value="low">🟢 Faible (≤ 33)</SelectItem>
                <SelectItem value="medium_low">🟡 Faible + moyenne (≤ 66)</SelectItem>
                <SelectItem value="medium">🟠 Moyenne (34-66)</SelectItem>
                <SelectItem value="high">🔴 Forte uniquement (≥ 67)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={intentFilter} onValueChange={setIntentFilter}>
              <SelectTrigger className="w-[180px]" data-testid="intent-filter">
                <SelectValue placeholder="Intention" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes intentions</SelectItem>
                <SelectItem value="transactional">Transactionnel</SelectItem>
                <SelectItem value="commercial">Commercial</SelectItem>
                <SelectItem value="local">Local</SelectItem>
                <SelectItem value="navigational">Navigation</SelectItem>
                <SelectItem value="brand">Marque</SelectItem>
                <SelectItem value="informational">Information</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger className="w-[250px]" data-testid="client-select">
                <SelectValue placeholder="Sélectionner un client" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              variant="outline"
              onClick={fetchOpportunities}
              disabled={loading || !selectedClient}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Analyser
            </Button>
          </div>
        </div>

        {/* Zones cibles client */}
        {selectedClient && opportunities.length > 0 && (
          <div className="bg-white border border-neutral-200 rounded p-3 flex items-center gap-3 flex-wrap text-sm" data-testid="target-zones-banner">
            <span className="text-neutral-500 font-medium">Zones cibles client :</span>
            {targetZones.length > 0 ? (
              targetZones.map(z => (
                <span key={z} className="px-2 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded text-xs">
                  {z}
                </span>
              ))
            ) : (
              <span className="text-amber-700 italic">
                Aucune zone définie — le score n'est pas adapté au territoire du client. Renseignez "Zones cibles" dans la fiche client.
              </span>
            )}
            {(selectedClientData?.excluded_terms?.length > 0 || selectedClientData?.focus_terms?.length > 0) && (
              <span className="ml-auto flex items-center gap-2 text-xs">
                {selectedClientData?.excluded_terms?.length > 0 && (
                  <span className="px-2 py-1 bg-red-50 text-red-700 border border-red-200 rounded" data-testid="excluded-count-badge">
                    {selectedClientData.excluded_terms.length} exclu{selectedClientData.excluded_terms.length > 1 ? 's' : ''}
                  </span>
                )}
                {selectedClientData?.focus_terms?.length > 0 && (
                  <span className="px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded" data-testid="focus-count-badge">
                    {selectedClientData.focus_terms.length} focus
                  </span>
                )}
              </span>
            )}
          </div>
        )}

        {/* Warning Banner */}
        <div className="bg-yellow-50 border border-yellow-200 rounded p-4 flex items-start gap-3">
          <Lock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-yellow-800">Rapport technique privé</p>
            <p className="text-sm text-yellow-700">
              Ces opportunités sont réservées à l'équipe technique. Ne pas partager avec les clients 
              pour éviter toute confusion avec vos recommandations stratégiques.
            </p>
          </div>
        </div>

        {/* Diagnostic Banner — shown only when provider returned no opportunities */}
        {diagnostic && !loading && opportunities.length === 0 && (diagnostic.error || diagnostic.raw_count > 0) && (
          <div
            className={`border rounded p-4 flex items-start gap-3 ${
              diagnostic.provider_configured ? 'bg-orange-50 border-orange-200' : 'bg-red-50 border-red-200'
            }`}
            data-testid="opportunities-diagnostic"
          >
            <Lightbulb className={`w-5 h-5 flex-shrink-0 mt-0.5 ${diagnostic.provider_configured ? 'text-orange-600' : 'text-red-600'}`} />
            <div className="space-y-1">
              <p className={`font-medium ${diagnostic.provider_configured ? 'text-orange-800' : 'text-red-800'}`}>
                Diagnostic {providerLabel || 'provider'}
              </p>
              {diagnostic.error && (
                <p className="text-sm text-neutral-700" data-testid="diagnostic-error">
                  <strong>Erreur :</strong> {diagnostic.error}
                </p>
              )}
              <p className="text-sm text-neutral-700">
                <strong>Mots-clés retournés par le provider :</strong> {diagnostic.raw_count}
                {diagnostic.filtered_out > 0 && (
                  <> · <strong>Déjà suivis (filtrés) :</strong> {diagnostic.filtered_out}</>
                )}
              </p>
              {diagnostic.response_keys?.length > 0 && (
                <p className="text-xs text-neutral-500 font-mono">
                  Clés réponse brute : [{diagnostic.response_keys.join(', ')}]
                </p>
              )}
            </div>
          </div>
        )}

        {/* Client Info */}
        {selectedClientData && (
          <div className="bg-white border border-neutral-200 p-4 flex items-center justify-between">
            <div>
              <p className="font-medium text-neutral-900">{selectedClientData.name}</p>
              <a 
                href={selectedClientData.website?.startsWith('http') ? selectedClientData.website : `https://${selectedClientData.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#002FA7] hover:underline flex items-center gap-1"
              >
                {selectedClientData.website}
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <div className="text-right">
              <p className="font-mono text-2xl font-bold text-[#002FA7]">{opportunities.length}</p>
              <p className="text-xs text-neutral-500">opportunités trouvées</p>
            </div>
          </div>
        )}

        {/* Score formula explainer — visible when there are results */}
        {opportunities.length > 0 && (
          <div className="bg-neutral-50 border border-neutral-200 rounded p-4 space-y-3" data-testid="score-formula-banner">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono px-2 py-0.5 rounded bg-[#002FA7] text-white">FORMULE DU SCORE</span>
              <span className="text-xs text-neutral-500">Comment lire le tableau ?</span>
            </div>
            <div className="font-mono text-sm bg-white border border-neutral-200 rounded p-3 overflow-x-auto" data-testid="score-formula">
              <span className="text-neutral-900 font-semibold">Score</span>
              {' = '}
              <span className="text-emerald-700">Volume</span>
              {' × '}
              <span className="text-emerald-700">CPC</span>
              {' × '}
              <span className="text-blue-700">Intention</span>
              {' × '}
              <span className="text-purple-700">Zone</span>
              {' × '}
              <span className="text-amber-700">Focus</span>
              {' × '}
              <span className="text-rose-700">Concurrence</span>
              <span className="text-neutral-500"> ÷ </span>
              <span className="text-neutral-700">√Position</span>
            </div>
            <ul className="text-xs text-neutral-600 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
              <li><span className="text-emerald-700 font-semibold">Volume</span> · recherches mensuelles Google</li>
              <li><span className="text-emerald-700 font-semibold">CPC</span> · valeur économique du clic (€)</li>
              <li><span className="text-blue-700 font-semibold">Intention</span> · transac ×3 · com ×2.5 · local ×2 · info ×0.6</li>
              <li><span className="text-purple-700 font-semibold">Zone</span> · ma zone ×5 · ma région ×2.5 · autre ×0.1</li>
              <li><span className="text-amber-700 font-semibold">Focus</span> · ×1.5 si match d'un terme focus client</li>
              <li><span className="text-rose-700 font-semibold">Concurrence</span> · facile ×1.5 · moyenne ×1 · saturée ×0.5</li>
              <li className="sm:col-span-2 lg:col-span-3"><span className="text-neutral-700 font-semibold">√Position</span> · rendements décroissants quand on est déjà bien classé (passer de 50→40 vaut plus que de 5→3)</li>
            </ul>
            <p className="text-xs text-neutral-500 italic pt-1 border-t border-neutral-200">
              💡 Le tri par défaut place les mots-clés où vous gagnerez le plus d'argent en moins d'efforts. Un score élevé = ROI maximal pour ce client.
            </p>
          </div>
        )}

        {/* Opportunities List */}
        <div className="bg-white border border-neutral-200">
          {loading ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-2 border-neutral-300 border-t-[#002FA7] rounded-full animate-spin mx-auto mb-4" />
              <p className="text-neutral-500" data-testid="opportunities-loading">
                Analyse en cours{providerLabel ? ` via ${providerLabel}` : ''}...
              </p>
              <p className="text-sm text-neutral-400">Cela peut prendre quelques secondes</p>
            </div>
          ) : !selectedClient ? (
            <div className="p-12 text-center">
              <Lightbulb className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
              <p className="text-neutral-500">Sélectionnez un client pour voir les opportunités</p>
            </div>
          ) : opportunities.length === 0 ? (
            <div className="p-12 text-center">
              <Lightbulb className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
              <p className="text-neutral-500">Aucune nouvelle opportunité trouvée</p>
              <p className="text-sm text-neutral-400 mt-1">
                Tous les mots-clés trouvés sont déjà suivis ou le site n'a pas assez de positions
              </p>
            </div>
          ) : (() => {
            const intentOK = (o) => intentFilter === 'all' || (o.intent || '') === intentFilter;
            const geoOK = (o) => {
              if (geoFilter === 'all') return true;
              if (geoFilter === 'my_zones') return o.geo_match === 'own_zone' || o.geo_match === 'own_region';
              if (geoFilter === 'local_relevant') return ['own_zone', 'own_region', 'local_generic'].includes(o.geo_match);
              if (geoFilter === 'national') return o.geo_match === 'national';
              if (geoFilter === 'exclude_others') return o.geo_match !== 'local_other';
              return true;
            };
            const competitionOK = (o) => {
              if (competitionFilter === 'all') return true;
              const pct = normalizeCompetition(o.competition);
              if (pct === null) return false; // hide unknown when user filters
              if (competitionFilter === 'low') return pct <= 33;
              if (competitionFilter === 'medium_low') return pct <= 66;
              if (competitionFilter === 'medium') return pct >= 34 && pct <= 66;
              if (competitionFilter === 'high') return pct >= 67;
              return true;
            };
            // 🎯 Top Quick Wins — Position 4-20 + Concurrence ≤ moyenne (≤66) + CPC > 1€
            const quickWinOK = (o) => {
              if (!quickWins) return true;
              if (!o.position || o.position < 4 || o.position > 20) return false;
              const pct = normalizeCompetition(o.competition);
              if (pct !== null && pct > 66) return false;
              if (!o.cpc || o.cpc <= 1) return false;
              return true;
            };
            const filtered = opportunities.filter(o => intentOK(o) && geoOK(o) && competitionOK(o) && quickWinOK(o));
            // Apply sort if user clicked a column header
            // (Top Quick Wins implicitly sorts by score desc when no manual sort is set)
            const effectiveSortKey = sortKey || (quickWins ? 'score' : null);
            const effectiveSortDir = sortKey ? sortDir : 'desc';
            if (effectiveSortKey) {
              filtered.sort((a, b) => {
                const va = sortValue(a, effectiveSortKey);
                const vb = sortValue(b, effectiveSortKey);
                // Null sentinel = missing value → always sink to bottom in
                // both asc and desc (more useful than alphabetical 0 first).
                const aNull = va === null || va === undefined;
                const bNull = vb === null || vb === undefined;
                if (aNull && bNull) return 0;
                if (aNull) return 1;
                if (bNull) return -1;
                let cmp;
                if (typeof va === 'string') cmp = va.localeCompare(vb);
                else cmp = (va || 0) - (vb || 0);
                return effectiveSortDir === 'asc' ? cmp : -cmp;
              });
            }
            if (filtered.length === 0) {
              return (
                <div className="p-12 text-center" data-testid="empty-filtered">
                  <Lightbulb className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
                  <p className="text-neutral-500">
                    Aucune opportunité avec ces filtres
                  </p>
                  <p className="text-sm text-neutral-400 mt-1">
                    {targetZones.length === 0 && (geoFilter === 'my_zones' || geoFilter === 'local_relevant')
                      ? 'Aucune zone définie sur ce client. Renseignez "Zones cibles" dans la fiche client pour activer ce filtre.'
                      : 'Essayez d\'élargir les filtres.'}
                  </p>
                </div>
              );
            }
            return (
            <>
            {quickWins && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3 flex items-start gap-2 text-sm" data-testid="quick-wins-banner">
                <Target className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <span className="font-medium text-emerald-900">Mode Top Quick Wins actif</span>
                  <span className="text-emerald-800/80"> — affichage des mots-clés en <strong>position 4-20</strong> avec <strong>concurrence ≤ moyenne</strong> et <strong>CPC &gt; 1 €</strong>. {filtered.length} candidat(s) trouvé(s), classé(s) par score ROI.</span>
                </div>
                <button
                  onClick={() => setQuickWins(false)}
                  className="text-[11px] text-emerald-700 underline hover:text-emerald-900"
                  data-testid="quick-wins-clear"
                >
                  désactiver
                </button>
              </div>
            )}
            <table className="w-full data-table" data-testid="opportunities-table">
              <thead>
                <tr>
                  <SortableTh k="keyword" label="Mot-clé" />
                  <SortableTh k="zone" label="Zone" tooltip="Trier par pertinence géographique (zone cible client)" />
                  <SortableTh k="intent" label="Intention" tooltip="Trier par valeur économique de l'intention (transactionnel > commercial > local > info)" />
                  <th className="text-right">
                    <HoverCard openDelay={150}>
                      <HoverCardTrigger asChild>
                        <button
                          type="button"
                          onClick={() => handleSort('score')}
                          className={`inline-flex items-center gap-1 cursor-pointer hover:text-[#002FA7] transition-colors flex-row-reverse w-full justify-end ${sortKey === 'score' ? 'text-[#002FA7] font-semibold' : ''}`}
                          data-testid="sort-score"
                        >
                          {sortKey === 'score' ? (sortDir === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                          <span>Score</span>
                          <Info className="w-3.5 h-3.5 text-neutral-400" />
                        </button>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-80 text-left" align="end">
                        <div className="space-y-2 text-sm">
                          <p className="font-semibold text-neutral-900">Score de priorité ROI</p>
                          <p className="text-xs text-neutral-600">
                            Estimation du potentiel revenu d'un mot-clé pour CE client.
                          </p>
                          <div className="bg-neutral-50 border border-neutral-200 rounded p-2 font-mono text-[11px] text-neutral-800">
                            (Volume × CPC × Intention × Zone × Focus × Concurrence) ÷ √Position
                          </div>
                          <ul className="text-xs space-y-1 text-neutral-600">
                            <li><strong>Volume</strong> : recherches/mois</li>
                            <li><strong>CPC</strong> : valeur économique du clic</li>
                            <li><strong>Intention</strong> : transac ×3, com ×2.5, local ×2, info ×0.6</li>
                            <li><strong>Zone</strong> : ma zone ×5, ma région ×2.5, autre zone ×0.1</li>
                            <li><strong>Focus</strong> : ×1.5 si match d'un terme focus client</li>
                            <li><strong>Concurrence</strong> : facile ×1.5, moyenne ×1, saturée ×0.5</li>
                            <li><strong>√Position</strong> : rendements décroissants si déjà bien classé</li>
                          </ul>
                          <p className="text-[11px] text-neutral-500 italic pt-1 border-t border-neutral-200">
                            Plus le score est élevé, plus le ROI potentiel est important.
                          </p>
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  </th>
                  <SortableTh k="position" label="Position" align="right" className="text-right" />
                  <SortableTh k="volume" label="Volume" align="right" className="text-right" tooltip="Trier par recherches mensuelles" />
                  <SortableTh k="cpc" label="CPC" align="right" className="text-right" tooltip="Coût par clic moyen — valeur économique du clic. Trier décroissant = mots-clés les plus monétisables." />
                  <SortableTh k="competition" label="Concurrence" tooltip="Niveau de concurrence SEO du mot-clé. Plus c'est faible, plus le mot-clé est rapide à conquérir." />
                  <SortableTh k="potential" label="Potentiel" tooltip="Verdict global haut/moyen/bas (combine score + position + volume)" />
                  <th>URL</th>
                  <th className="w-32"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((opp) => (
                  <tr key={opp.keyword}>
                    <td>
                      <span className="font-medium text-neutral-900 inline-flex items-center gap-1">
                        {opp.keyword}
                        {opp.focus_match && (
                          <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400" data-testid="focus-star" title="Terme focus client (×1.5)" />
                        )}
                      </span>
                    </td>
                    <td>{getGeoBadge(opp)}</td>
                    <td>{getIntentBadge(opp.intent)}</td>
                    <td className="text-right">
                      {getScoreBadge(opp.priority_score)}
                    </td>
                    <td className="text-right">
                      <span className="font-mono text-lg font-bold text-neutral-900">
                        {opp.position || '-'}
                      </span>
                    </td>
                    <td className="text-right font-mono text-neutral-600">
                      {opp.search_volume?.toLocaleString() || '-'}
                    </td>
                    <td className="text-right font-mono">
                      {(() => {
                        const cpcNum = opp.cpc == null ? null : Number(opp.cpc);
                        return cpcNum != null && Number.isFinite(cpcNum) && cpcNum > 0
                          ? <span className="text-neutral-900">{cpcNum.toFixed(2)} €</span>
                          : <span className="text-neutral-300">—</span>;
                      })()}
                    </td>
                    <td>{getCompetitionBadge(opp.competition)}</td>
                    <td>{getPotentialBadge(opp.potential)}</td>
                    <td className="max-w-[200px]">
                      {opp.url && (
                        <a 
                          href={opp.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-[#002FA7] hover:underline truncate block"
                        >
                          {opp.url.replace(/^https?:\/\/[^/]+/, '')}
                        </a>
                      )}
                    </td>
                    <td>
                      {addedKeywords.has(opp.keyword) ? (
                        <span className="flex items-center gap-1 text-green-600 text-sm">
                          <Check className="w-4 h-4" />
                          Ajouté
                        </span>
                      ) : (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => addAsKeyword(opp, false)}
                            disabled={addingKeyword === opp.keyword}
                          >
                            {addingKeyword === opp.keyword ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Plus className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            className="bg-[#002FA7] hover:bg-[#002277]"
                            onClick={() => addAsKeyword(opp, true)}
                            disabled={addingKeyword === opp.keyword}
                            title="Ajouter comme prioritaire"
                          >
                            <TrendingUp className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </>
            );
          })()}
        </div>
      </div>
    </Layout>
  );
}
